import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ICompanySlideoutProps } from "../../CompanySlideout/CompanySlideout";
import { RootState } from "../../../store";
import { matchesApi } from "../../../services/matches";

type ICompanySlideoutValues = Omit<ICompanySlideoutProps, 'handleNavigateAway' | 'joid'>;
export type AssignmentsSlideOut = ICompanySlideoutValues[] | undefined;

export type AssignmentsLookup = {
  isOpen: boolean;
};

export interface CredentialAppState {
  selectedUserId: string | undefined;
  assignmentsSlideOut: AssignmentsSlideOut;
  assignmentsLookup: AssignmentsLookup;
  selectedFilterGroup: string | undefined;
  sid: string | null;
  currentPage: number;
  saveCredentialStatus: 'fulfilled' | 'rejected' | 'pending';
  browserAllState: {
    sectionId: string;
  } | undefined;
}

const initialState: CredentialAppState = {
  selectedUserId: undefined,
  assignmentsSlideOut: undefined,
  assignmentsLookup: {
    isOpen: false,
  },
  selectedFilterGroup: undefined,
  sid: null,
  currentPage: 1,
  saveCredentialStatus: 'fulfilled',
  browserAllState: undefined,
};

export const credentialsAppSlice = createSlice({
  name: "credentialsAppState",
  initialState,
  reducers: {
    setSelectedUserId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedUserId = action.payload;
    },
    addAssignmentsSlideOut: (
      state,
      action: PayloadAction<ICompanySlideoutValues>
    ) => {
      if (state.assignmentsSlideOut === undefined) {
        state.assignmentsSlideOut = [action.payload];
      } else if (
        Array.isArray(state.assignmentsSlideOut) &&
        state.assignmentsSlideOut.at(-1)?.companyId !==
          action.payload?.companyId
      ) {
        // check if last elem in array is not the same as the incoming elem
        state.assignmentsSlideOut.push(action.payload);
      }
    },
    removeCurrentProfileDrawer: (state) => {
      state.assignmentsSlideOut?.pop();
    },
    closeAllProfileDrawer: (state) => {
      state.assignmentsSlideOut = undefined;
    },
    showAssignmentsLookup: (
      state,
    ) => {
      state.assignmentsLookup.isOpen = true;
    },
    hideAssignmentsLookup: (state) => {
      state.assignmentsLookup.isOpen = false;
    },
    updateSelectedFilterGroup: (state, action: PayloadAction<string>) => {
      state.selectedFilterGroup = action.payload;
    },
    updateSid: (state, action: PayloadAction<string | null>) => {
      state.sid = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    resetAssignmentSearchSession: (state) => {
      state.currentPage = 1;
      state.sid = null;
    },
    closeBrowseAll: (state) => {
      state.browserAllState = undefined;
    },
    showBrowseAll: (state, action: PayloadAction<{ sectionId: string }>) => {
      state.browserAllState = {
        sectionId: action.payload.sectionId,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(matchesApi.endpoints.updateCred.matchFulfilled, (state, action) => {
        state.saveCredentialStatus = action.meta.requestStatus;
      })
      .addMatcher(matchesApi.endpoints.updateCred.matchPending, (state, action) => {
        state.saveCredentialStatus = action.meta.requestStatus;
      })
      .addMatcher(matchesApi.endpoints.updateCred.matchRejected, (state, action) => {
        // RTK Query reports updateRequest.abort() as rejected. For example,
        // cancelling stale autosave A before save B is intentional and should
        // return the header to "Saved"; only a real rejection should show Error.
        state.saveCredentialStatus = action.meta.aborted
          ? 'fulfilled'
          : action.meta.requestStatus;
      })
  }
});

// Accessors
export const selectAssignmentsSlideOut = (state: RootState) =>
  state.credentialsAppState.assignmentsSlideOut?.at(-1);
export const selectAssignmentsLookup = (state: RootState) =>
  state.credentialsAppState.assignmentsLookup;
export const selectSaveCredentialStatus = (state: RootState) =>
  state.credentialsAppState.saveCredentialStatus;
export const selectSelectedUserId = (state: RootState) =>
  state.credentialsAppState.selectedUserId;

export const getSelectedFilterGroup = (state: RootState) =>
  state.credentialsAppState.selectedFilterGroup;

export const getBrowseAllState = (state: RootState) =>
  state.credentialsAppState.browserAllState;

export const {
  setSelectedUserId,
  addAssignmentsSlideOut,
  removeCurrentProfileDrawer,
  closeAllProfileDrawer,
  showAssignmentsLookup,
  hideAssignmentsLookup,
  updateSelectedFilterGroup,
  updateSid,
  setCurrentPage,
  resetAssignmentSearchSession,
  closeBrowseAll,
  showBrowseAll,
} = credentialsAppSlice.actions;

export default credentialsAppSlice.reducer;
